import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Read and write one query-string parameter as if it were component state.
 *
 * The point is reload and link-sharing: a filter that lives only in useState
 * is lost on refresh and cannot be sent to anyone, which is exactly the wrong
 * behaviour for a diagnostic view someone is about to paste into a ticket.
 *
 * Two rules the callers depend on:
 *  - Writes MERGE. Every setter preserves the other params, so the dashboard's
 *    ?sigfoxId= survives a change to an unrelated filter.
 *  - A value equal to the default REMOVES the param rather than writing it.
 *    Otherwise every default-state page accumulates a tail of redundant query
 *    string, and two URLs that mean the same thing look different.
 *
 * Navigation is `replace`, not `push`: toggling a filter should not have to be
 * undone with six presses of the back button.
 */
export function useUrlState<T>(
  key: string,
  defaultValue: T,
  parse: (raw: string) => T,
  serialize: (value: T) => string
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams()

  const raw = searchParams.get(key)
  const value = raw === null ? defaultValue : parse(raw)

  const setValue = useCallback(
    (next: T) => {
      // This router version has no functional updater, so the merge reads the
      // current params from the closure — hence searchParams in the deps.
      const params = new URLSearchParams(searchParams)
      if (serialize(next) === serialize(defaultValue)) params.delete(key)
      else params.set(key, serialize(next))
      setSearchParams(params, { replace: true })
    },
    // `parse`/`serialize` are expected to be stable module-level functions;
    // including them would re-create the setter on every render for callers
    // that pass inline arrows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, defaultValue, searchParams, setSearchParams]
  )

  return [value, setValue]
}

/** Number in the URL, falling back to `defaultValue` for anything unparseable. */
export function useUrlNumber(key: string, defaultValue: number): [number, (n: number) => void] {
  return useUrlState<number>(
    key,
    defaultValue,
    (raw) => (Number.isFinite(Number(raw)) ? Number(raw) : defaultValue),
    (n) => String(n)
  )
}

/** Boolean in the URL, present as '1' when true and absent when false. */
export function useUrlBoolean(key: string, defaultValue: boolean): [boolean, (b: boolean) => void] {
  return useUrlState<boolean>(
    key,
    defaultValue,
    (raw) => raw === '1',
    (b) => (b ? '1' : '0')
  )
}

/**
 * Account selector: absent means every account, the literal 'none' means the
 * devices that have no account at all, and anything else is an account id.
 * `null` and `undefined` mean different things here, so this cannot use
 * useUrlState's equality-to-default shortcut.
 */
export function useUrlAccount(key = 'account'): [number | null | undefined, (next: number | null | undefined) => void] {
  const [searchParams, setSearchParams] = useSearchParams()

  const raw = searchParams.get(key)
  const value = raw === null ? undefined : raw === 'none' ? null : Number(raw)

  const setValue = useCallback(
    (next: number | null | undefined) => {
      const params = new URLSearchParams(searchParams)
      if (next === undefined) params.delete(key)
      else params.set(key, next === null ? 'none' : String(next))
      setSearchParams(params, { replace: true })
    },
    [key, searchParams, setSearchParams]
  )

  return [Number.isNaN(value as number) ? undefined : value, setValue]
}

export interface DayRange {
  from: string
  to: string
}

/**
 * A zoom window in the URL as `from..to`, absent when not zoomed.
 *
 * One param rather than two so clearing the zoom cannot leave half of it
 * behind, and so a malformed value fails as a whole instead of silently
 * zooming to one edge.
 */
export function useUrlDayRange(key = 'zoom'): [DayRange | null, (next: DayRange | null) => void] {
  const [searchParams, setSearchParams] = useSearchParams()

  const raw = searchParams.get(key)
  let value: DayRange | null = null
  if (raw) {
    const [from, to] = raw.split('..')
    if (/^\d{4}-\d{2}-\d{2}$/.test(from ?? '') && /^\d{4}-\d{2}-\d{2}$/.test(to ?? '')) {
      value = from <= to ? { from, to } : { from: to, to: from }
    }
  }

  const setValue = useCallback(
    (next: DayRange | null) => {
      const params = new URLSearchParams(searchParams)
      if (next === null) params.delete(key)
      else params.set(key, `${next.from}..${next.to}`)
      setSearchParams(params, { replace: true })
    },
    [key, searchParams, setSearchParams]
  )

  return [value, setValue]
}
